// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CART_BREAKPOINT } from "@eisinc/XDL/constants";
import { color, xClassNames, xScreen } from "@eisinc/XDL/Utilities";
import { mergeWithCss } from "@eisinc/XDL/Utilities/mergeWithCss";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Children, FC, PropsWithChildren, useState } from "react";
import { css } from "styled-components";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    includeActions?: boolean;
    includeReqDeliveryDate?: boolean;
    includeQtyOrderedShipped?: boolean;
    [key: string]: any;
}
interface OwnProps {
    Image: React.FC<PropsWithChildren>;
    Description: React.FC<PropsWithChildren>;
    PriceUnit: React.FC<PropsWithChildren>;
    Qty: React.FC<PropsWithChildren>;
    Total: React.FC<PropsWithChildren>;
    Remove: React.FC<PropsWithChildren>;
    Actions: React.FC<PropsWithChildren>;
    Notes: React.FC<PropsWithChildren>;
    ReqDeliveryDate: React.FC<PropsWithChildren>;
}
export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    priceUnit?: InjectableCss;
    qty?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
    notes?: InjectableCss;
    remove?: InjectableCss;
    priceQtyWrapper?: InjectableCss;
    reqDeliveryDate?: InjectableCss;
}

export const cartlineContainerStyles: CartLineContainerStyles = {
    container: {
        css: css`
            width: 100%;
            border-bottom: 1px solid ${color("common.ui3")};
            padding: 12px 0 12px 8px;
            display: grid;
            gap: 8px 16px;
            grid-template-columns:
                92px minmax(294px, 1fr) minmax(0, 108px) minmax(92px, 98px) minmax(0, 164px) minmax(0, 136px)
                23px;

            ${xScreen("lte", CART_BREAKPOINT)} {
                grid-template-columns:
                    92px minmax(120px, 1fr) minmax(0, 108px) minmax(92px, 98px) minmax(0, 164px) minmax(0, 136px)
                    23px;
            }

            ${xScreen("lte", "md")} {
                grid-template-columns: 92px minmax(64px, 1fr) minmax(94px, 1fr) minmax(61px, 1fr);
                gap: 16px;
            }

            ${xScreen("lte", "xs")} {
                padding: 12px 0;
                grid-template-columns: 92px minmax(64px, 1fr) minmax(94px, 1fr);
            }
        `,
    },
    image: {
        css: css`
            grid-column: 1 / 2;
            grid-row: 1/4;
            display: flex;
            flex-direction: column;
            row-gap: 6px;
            figure {
                margin: 0;
            }
            .product-image-print {
                max-width: 92px;
                max-height: 92px;

                @media screen {
                    display: none;
                }
            }

            ${xScreen("lte", "md")} {
                margin-bottom: -4px;
                grid-row: 1/2;
            }
        `,
    },
    description: {
        css: css`
            grid-column: 2 / 3;
            grid-row: 1 / 2;
            position: relative;
            display: flex;
            flex-direction: column;
            margin-left: -4px;

            ${({ includeReqDeliveryDate, includeQtyOrderedShipped }: any) =>
                !includeReqDeliveryDate && !includeQtyOrderedShipped && "grid-column: 2 / 4;"}

            ${xScreen("lte", "md")} {
                grid-column: 2 / -1;
                grid-row: 1 / 2;
                margin-bottom: -4px;
            }
        `,
    },
    reqDeliveryDate: {
        css: css`
            grid-column: 3 / 4;
            grid-row: 1 / 2;
            ${xScreen("lte", "md")} {
                grid-column: 1 / 3;
                grid-row: 2 / 3;
            }
        `,
    },
    priceUnit: {
        css: css`
            grid-column: 5/6;
            grid-row: 1/2;
            ${xScreen("lte", "md")} {
                grid-row: 3/4;
                grid-column: 1/3;
            }
        `,
    },
    qty: {
        css: css`
            ${({ includeQtyOrderedShipped }: any) =>
                includeQtyOrderedShipped
                    ? css`
                          grid-column: 3 / 5;
                          grid-row: 1 / 2;

                          ${xScreen("lte", "md")} {
                              grid-column: 1 / 5;
                              grid-row: 2 / 3;
                          }
                          ${xScreen("lte", "xs")} {
                              grid-column: 1 / 4;
                          }
                      `
                    : css`
                          grid-column: 4/5;
                          grid-row: 1 / 2;

                          ${xScreen("lte", "md")} {
                              grid-column: 3 / 5;
                              grid-row: 2 / 3;
                          }
                          ${xScreen("lte", "xs")} {
                              grid-column: 3 / 4;
                          }
                      `}
        `,
    },
    total: {
        css: css`
            grid-column: 6/7;
            grid-row: 1/2;
            ${xScreen("lte", "md")} {
                grid-row: 3/4;
                grid-column: 3/5;
            }
            ${xScreen("lte", "xs")} {
                grid-column: 3 / 4;
            }
        `,
    },
    remove: {
        css: css`
            grid-column: 7/8;
            grid-row: 1/2;
            align-items: flex-start;

            ${xScreen("lte", "md")} {
                grid-row: 4/5;
                grid-column: 4/5;
                display: flex;
                justify-content: flex-end;
            }
            ${xScreen("lte", "xs")} {
                grid-column: 3 / 4;
            }
        `,
    },
    // add to list and checkboxes
    actions: {
        css: css`
            grid-column: 5 / -1;
            grid-row: 2 / 3;

            &:empty {
                display: none;
            }
            ${xScreen("lte", "md")} {
                grid-column: 1 / -1;
                grid-row: 5 / 6;
            }
        `,
    },
    notes: {
        css: css`
            &:empty {
                display: none;
            }
            display: flex;
            flex-direction: column;
            gap: 12px;

            grid-column: 2/3;
            grid-row: 2/3;
            margin-left: -4px;
            ${xScreen("lte", "md")} {
                grid-row: 4/5;
                grid-column: 1/4;
                margin-left: 0;
                max-width: 255px;
            }
            ${xScreen("lte", "xs")} {
                grid-column: 1 / 3;
            }
        `,
    },
};

const names = {
    Image: "Image",
    Description: "Description",
    PriceUnit: "PriceUnit",
    Qty: "Qty",
    Total: "Total",
    Remove: "Remove",
    Actions: "Actions",
    Notes: "Notes",
    ReqDeliveryDate: "ReqDeliveryDate",
};

const Image: FC<PropsWithChildren> = () => null;
const Description: FC<PropsWithChildren> = () => null;
const PriceUnit: FC<PropsWithChildren> = () => null;
const Qty: FC<PropsWithChildren> = () => null;
const Total: FC<PropsWithChildren> = () => null;
const Remove: FC<PropsWithChildren> = () => null;
const Actions: FC<PropsWithChildren> = () => null;
const Notes: FC<PropsWithChildren> = () => null;
const ReqDeliveryDate: FC<PropsWithChildren> = () => null;

const CartLineContainer: React.FC<PropsWithChildren<CartLineContainerProps>> & OwnProps = ({
    children,
    extendedStyles,
    includeActions = true,
    includeReqDeliveryDate,
    includeQtyOrderedShipped,
    ...otherProps
}) => {
    const [styles] = useState(() => mergeWithCss(cartlineContainerStyles, extendedStyles));

    const { Image, Description, PriceUnit, Qty, Total, Remove, Actions, Notes, ReqDeliveryDate } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
        const MappedChilds = childrenArray.reduce(
            (results, current) => {
                const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
                if (currentName) {
                    results[currentName] = current;
                }
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren>>,
        );
        const slots = Object.entries(names).reduce(
            (results, [key, value], idx) => {
                results[value] = MappedChilds[value] || null;
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren> | null>,
        );

        return slots;
    }, [children]);

    const className = xClassNames.append("cartLine", otherProps.className);

    return (
        <StyledWrapper {...styles.container} {...{ includeActions }} className={className}>
            <StyledWrapper {...styles.image}>{Image?.props?.children}</StyledWrapper>
            <StyledWrapper {...styles.description} {...{ includeReqDeliveryDate, includeQtyOrderedShipped }}>
                {Description?.props?.children}
            </StyledWrapper>
            {includeReqDeliveryDate && (
                <StyledWrapper {...styles.reqDeliveryDate}>{ReqDeliveryDate?.props?.children}</StyledWrapper>
            )}
            <StyledWrapper {...styles.priceUnit} className="price-unit">
                {PriceUnit?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.qty} className="qty" {...{ includeQtyOrderedShipped }}>
                {Qty?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.total} className="total">
                {Total?.props?.children}
            </StyledWrapper>
            {includeActions && <StyledWrapper {...styles.remove}>{Remove?.props?.children}</StyledWrapper>}
            {Actions?.props?.children && <StyledWrapper {...styles.actions}>{Actions?.props?.children}</StyledWrapper>}
            <StyledWrapper {...styles.notes} {...{ includeActions }}>
                {Notes?.props?.children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.PriceUnit = PriceUnit;
CartLineContainer.Qty = Qty;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Actions = Actions;
CartLineContainer.Notes = Notes;
CartLineContainer.ReqDeliveryDate = ReqDeliveryDate;

CartLineContainer.Image.displayName = names.Image;
CartLineContainer.Description.displayName = names.Description;
CartLineContainer.PriceUnit.displayName = names.PriceUnit;
CartLineContainer.Qty.displayName = names.Qty;
CartLineContainer.Total.displayName = names.Total;
CartLineContainer.Remove.displayName = names.Remove;
CartLineContainer.Actions.displayName = names.Actions;
CartLineContainer.Notes.displayName = names.Notes;
CartLineContainer.ReqDeliveryDate.displayName = names.ReqDeliveryDate;

export default CartLineContainer;
